<template>
  <div class="section-container">
    <div class="section-header">
      <h1 class="section-title">課程管理</h1>
      <div class="table-controls">
        <div class="search-add-group">
          <input
            type="text"
            v-model="filterText"
            placeholder="搜索课程..."
            class="search-input"
          />
          <button class="button button-primary" @click="openModal(null)">新增課程</button>
        </div>  
      </div>
    </div>  

    <div class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th @click="sortBy('course_id')">
              ID
              <span v-if="sortColumn === 'course_id'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('name')">
              課程名稱
              <span v-if="sortColumn === 'name'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('subject')">
              科目
              <span v-if="sortColumn === 'subject'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('grade')">
              年級
              <span v-if="sortColumn === 'grade'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th @click="sortBy('status')">
              狀態
              <span v-if="sortColumn === 'status'" class="sort-icon">
                {{ sortDirection === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
            <th>上課日</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="course in sortedAndFilteredCourses" 
            :key="course.course_id"
            @click="handleRowClick(course.course_id, $event)"
            class="clickable-row"
          >
            <td>{{ course.course_id }}</td>
            <td>{{ course.name }}</td>
            <td>{{ course.subject }}</td>
            <td>{{ getGradeLabel(course.grade) }}</td>
            <td>
              <span :class="`status-badge ${course.status.toLowerCase()}`">
                {{ getStatusLabel(course.status) }}
              </span>
            </td>
            <td>
              <div class="day-emoji-container">
                <span v-if="course.on_monday" title="Monday">1️⃣</span>
                <span v-if="course.on_tuesday" title="Tuesday">2️⃣</span>
                <span v-if="course.on_wednesday" title="Wednesday">3️⃣</span>
                <span v-if="course.on_thursday" title="Thursday">4️⃣</span>
                <span v-if="course.on_friday" title="Friday">5️⃣</span>
                <span v-if="course.on_saturday" title="Saturday">6️⃣</span>
                <span v-if="course.on_sunday" title="Sunday">7️⃣</span>
              </div>
            </td>
            <td class="actions" @click.stop>
              <button class="edit-btn" @click="openModal(course)">編輯</button>
              <button class="delete-btn" @click="confirmDelete(course.course_id)">刪除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <CourseModal
      v-if="showModal"
      :course="currentCourse"
      :isEdit="isEditMode"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { GET_COURSES, DELETE_COURSE } from '../graphql/queries';
import CourseModal from './CourseModal.vue';

const router = useRouter();
const { result, refetch } = useQuery(GET_COURSES);
const { mutate: deleteCourse } = useMutation(DELETE_COURSE);

// Modal state
const showModal = ref(false);
const currentCourse = ref(null);
const isEditMode = ref(false);

// Sorting state
const sortColumn = ref('course_id');
const sortDirection = ref('asc');

// Filter state
const filterText = ref('');

// Computed properties
const courses = computed(() => result.value?.courses || []);

// Combined sorted and filtered courses
const sortedAndFilteredCourses = computed(() => {
  let filtered = courses.value;
  // Apply filtering if search text exists
  const searchText = filterText.value.trim().toLowerCase();
  if (searchText) {
    filtered = filtered.filter(course =>
      course.name.toLowerCase().includes(searchText) ||
      course.subject.toLowerCase().includes(searchText) ||
      getGradeLabel(course.grade).toLowerCase().includes(searchText) ||
      getStatusLabel(course.status).toLowerCase().includes(searchText) ||
      getCourseDays(course).some(day => day.toLowerCase().includes(searchText))
    );
  }
  
  // Apply sorting
  return [...filtered].sort((a, b) => {
    let valA, valB;
    
    switch (sortColumn.value) {
      case 'course_id':
        valA = parseInt(a.course_id);
        valB = parseInt(b.course_id);
        break;
      case 'grade':
        valA = a.grade;
        valB = b.grade;
        break;
      case 'status':
        valA = getStatusLabel(a.status);
        valB = getStatusLabel(b.status);
        break;
      default:
        valA = a[sortColumn.value];
        valB = b[sortColumn.value];
    }
    
    if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }
    
    if (valA < valB) {
      return sortDirection.value === 'asc' ? -1 : 1;
    }
    if (valA > valB) {
      return sortDirection.value === 'asc' ? 1 : -1;
    }
    return 0;
  });
});

const gradeOptions = [
  { label: '一年级', value: 1 },
  { label: '二年级', value: 2 },
  { label: '三年级', value: 3 },
  { label: '四年级', value: 4 },
  { label: '五年级', value: 5 },
  { label: '六年级', value: 6 },
  { label: '國一', value: 7 },
  { label: '國二', value: 8 },
  { label: '國三', value: 9 },
  { label: '高一', value: 10 },
  { label: '高二', value: 11 },
  { label: '高三', value: 12 },
];

const statusLabels = {
  PREPARING: '準備中',
  ONGOING: '上課中',
  COMPLETED: '已結束',
  PAUSED: '暫停'
};

// Sorting function
function sortBy(column) {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortDirection.value = 'asc';
  }
}

function getGradeLabel(grade) {
  const option = gradeOptions.find(opt => opt.value === grade);
  return option ? option.label : grade;
}

function getStatusLabel(status) {
  return statusLabels[status] || status;
}

function getCourseDays(course) {
  const daysMapping = {
    on_monday: '週一',
    on_tuesday: '週二',
    on_wednesday: '週三',
    on_thursday: '週四',
    on_friday: '週五',
    on_saturday: '週六',
    on_sunday: '週日',
  };

  return Object.entries(daysMapping)
    .filter(([key]) => course[key])
    .map(([, day]) => day);
}

function handleRowClick(courseId, event) {
  const isButtonClick = event.target.closest('button') !== null;
  if (!isButtonClick) {
    router.push(`/courses/${courseId}`);
  }
}

function openModal(course) {
  currentCourse.value = course;
  isEditMode.value = !!course;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

function handleSaved() {
  refetch();
  closeModal();
}

function confirmDelete(courseId) {
  if (confirm('确定要删除这个课程吗？此操作不可撤销。')) {
    deleteCourse({ course_id: courseId }).then(() => {
      refetch();
    });
  }
}
</script>

<style scoped>
.table-container {
  overflow-x: auto;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: white;
}

.status-badge.preparing {
  background-color: #FF9800;
}

.status-badge.ongoing {
  background-color: #4CAF50;
}

.status-badge.completed {
  background-color: #9E9E9E;
}

.status-badge.paused {
  background-color: #F44336;
}

.day-emoji-container {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.actions {
  display: flex;
  gap: 8px;
  cursor: default;
}

.edit-btn {
  background-color: #2196F3;
  color: white;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.delete-btn {
  background-color: #F44336;
  color: white;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.table-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.search-input {
  padding: 0.5rem 1rem;
  height: 32px; /* Match button height */
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 0.9rem;
  width: 220px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

/* Make the search and button appear as a cohesive pair */
.search-add-group {
  display: flex;
  align-items: center;
  gap: 0;
}

.search-add-group .search-input {
  border-radius: 4px 0 0 4px;
  border-right: none;
}

.search-add-group .button {
  border-radius: 0 4px 4px 0;
}

/* Action buttons specific to course table */
.action-buttons {
  display: flex;
  gap: 0.5rem;
}

/* Sorting styles */
th {
  cursor: pointer;
  position: relative;
  user-select: none;
}

th:hover {
  background-color: var(--light-bg);
}

.sort-icon {
  margin-left: 4px;
  font-size: 0.8em;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-controls {
    flex-direction: column;
    align-items: flex-end;
    gap: 0.75rem;
    width: 100%;
  }
  
  .search-add-group {
    width: 100%;
  }
  
  .search-add-group .search-input {
    flex-grow: 1;
    width: auto;
  }
}
</style>